import { useState, useEffect, useCallback } from 'react';
import { Check, ChevronsUpDown, X, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AsyncScopeCombobox({
  token,
  endpoint,
  extractItems,
  value,
  onSelect,
  placeholder = 'Rechercher...',
  emptyText = 'Aucun resultat.',
  labelFn,
  icon: Icon,
  activeClass = 'border-primary bg-primary/5',
  testId = 'async-scope-combobox',
  searchParam = 'search',
  limitParam = 'per_page',
  limitValue = 10,
  initialLabel = null,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = { [limitParam]: limitValue };
      if (q && q.trim().length >= 1) params[searchParam] = q.trim();
      const { data } = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setItems(extractItems(data));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, endpoint, extractItems, searchParam, limitParam, limitValue]);

  // Debounced search on query change
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => search(query), 200);
    return () => clearTimeout(timer);
  }, [query, open, search]);

  // Load initial items when opening
  useEffect(() => {
    if (open) {
      search('');
    }
  }, [open, search]);

  // Fetch selected item label if value is set but we don't have it
  useEffect(() => {
    if (value && !selectedItem) {
      const found = items.find(i => i.id === value);
      if (found) setSelectedItem(found);
    }
  }, [value, items, selectedItem]);

  const handleSelect = (item) => {
    if (item.id === value) {
      onSelect(null, null);
      setSelectedItem(null);
    } else {
      onSelect(item.id, labelFn(item));
      setSelectedItem(item);
    }
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect(null, null);
    setSelectedItem(null);
    setOpen(false);
    setQuery('');
  };

  const displayLabel = selectedItem ? labelFn(selectedItem) : (value && initialLabel ? initialLabel : null);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-7 text-xs justify-between gap-1 px-2 max-w-[200px]',
            value ? activeClass : ''
          )}
          data-testid={testId}
        >
          {Icon && <Icon className="w-3 h-3 shrink-0" />}
          <span className="truncate">
            {displayLabel || placeholder.replace('...', '')}
          </span>
          {value ? (
            <X className="w-3 h-3 shrink-0 opacity-50 hover:opacity-100" onClick={handleClear} />
          ) : (
            <ChevronsUpDown className="w-3 h-3 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            className="h-8 text-xs"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : items.length === 0 ? (
              <CommandEmpty className="py-3 text-xs">{emptyText}</CommandEmpty>
            ) : (
              <CommandGroup>
                {items.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    onSelect={() => handleSelect(item)}
                    className="text-xs"
                    data-testid={`${testId}-item-${item.id}`}
                  >
                    <Check className={cn('w-3 h-3 shrink-0', value === item.id ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{labelFn(item)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
